import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  alpha,
  useTheme,
  Divider,
} from "@mui/material";
import {
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  LocalShipping as LocalShippingIcon,
  Warning as WarningIcon,
  Build as BuildIcon,
} from "@mui/icons-material";

/**
 * Dialog to show receiving and checking history for an OUTPUT line item
 *
 * Props:
 * - open: boolean
 * - onClose: function
 * - lineItem: The OUTPUT line item
 * - receives: Array of receive records with checkRecord
 * - onCheckBatch: function(receiveBatch) - callback to check a received batch
 */
function ReceivingHistoryDialog({
  open,
  onClose,
  lineItem,
  receives,
  onCheckBatch,
}) {
  const theme = useTheme();

  if (!lineItem) return null;

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getRemainingQty = (receive) =>
    Math.max(
      0,
      (receive.receivedQty || 0) -
        (receive.checkRecord?.checkedQty || 0) -
        (receive.checkRecord?.damagedQty || 0)
    );

  const getStatusChip = (receive) => {
    const remaining = getRemainingQty(receive);
    if (!receive.checkRecord) {
      return (
        <Chip
          label="Pending Check"
          size="small"
          color="warning"
          icon={<WarningIcon />}
          sx={{ height: 24 }}
        />
      );
    } else if (remaining > 0) {
      return (
        <Chip
          label="Partially Checked"
          size="small"
          color="info"
          icon={<WarningIcon />}
          sx={{ height: 24 }}
        />
      );
    } else {
      return (
        <Chip
          label="Checked"
          size="small"
          color="success"
          icon={<CheckCircleIcon />}
          sx={{ height: 24 }}
        />
      );
    }
  };

  const totalReceived =
    receives?.reduce((sum, r) => (!r.reconstruction ? sum + (r.receivedQty || 0) : sum), 0) || 0;
  const totalChecked =
    receives?.reduce((sum, r) => sum + (r.checkRecord?.checkedQty || 0), 0) || 0;
  const totalDamaged =
    receives?.reduce((sum, r) => sum + (r.checkRecord?.damagedQty || 0), 0) || 0;
  const totalNotTested = Math.max(0, totalReceived - totalChecked - totalDamaged);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <LocalShippingIcon color="primary" />
            <Typography variant="h6">Receiving History</Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* Item Summary */}
        <Paper
          elevation={0}
          sx={{
            mb: 3,
            p: 2,
            bgcolor: alpha(theme.palette.primary.main, 0.05),
            borderRadius: 2,
          }}
        >
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            {lineItem.item}
          </Typography>
          <Box sx={{ display: "flex", gap: 2, mt: 1 }}>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Ordered
              </Typography>
              <Typography variant="h6" fontWeight="600">
                {lineItem.qty}
              </Typography>
            </Box>
            <Divider orientation="vertical" flexItem />
            <Box>
              <Typography variant="caption" color="text.secondary">
                Total Received
              </Typography>
              <Typography variant="h6" fontWeight="600" color="primary.main">
                {totalReceived.toFixed(2)}
              </Typography>
            </Box>
            <Divider orientation="vertical" flexItem />
            <Box>
              <Typography variant="caption" color="text.secondary">
                Good Items
              </Typography>
              <Typography variant="h6" fontWeight="600" color="success.main">
                {totalChecked.toFixed(2)}
              </Typography>
            </Box>
            <Divider orientation="vertical" flexItem />
            <Box>
              <Typography variant="caption" color="text.secondary">
                Damaged
              </Typography>
              <Typography variant="h6" fontWeight="600" color="error.main">
                {totalDamaged.toFixed(2)}
              </Typography>
            </Box>
            {totalNotTested > 0 && (
              <>
                <Divider orientation="vertical" flexItem />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Not Yet Tested
                  </Typography>
                  <Typography variant="h6" fontWeight="600" color="warning.main">
                    {totalNotTested.toFixed(2)}
                  </Typography>
                </Box>
              </>
            )}
          </Box>
        </Paper>

        {/* Receiving History Table */}
        {!receives || receives.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <LocalShippingIcon
              sx={{ fontSize: 48, color: "text.disabled", mb: 1 }}
            />
            <Typography color="text.secondary">
              No receiving records yet
            </Typography>
          </Box>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow
                  sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}
                >
                  <TableCell>Batch ID</TableCell>
                  <TableCell>Received Date</TableCell>
                  <TableCell>Received By</TableCell>
                  <TableCell align="right">Qty Received</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Good</TableCell>
                  <TableCell align="right">Damaged</TableCell>
                  <TableCell align="right">Not Tested</TableCell>
                  <TableCell>Checked By</TableCell>
                  <TableCell>Checked Date</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {receives.map((receive) => (
                  <TableRow
                    key={receive.id}
                    sx={{
                      "&:hover": {
                        bgcolor: alpha(theme.palette.primary.main, 0.02),
                      },
                    }}
                  >
                    <TableCell>
                      <Chip
                        label={`#${receive.id}`}
                        size="small"
                        variant="outlined"
                        sx={{ height: 20, fontSize: "0.7rem" }}
                      />
                      {receive.reconstruction && (
                        <Tooltip title="Reconstructed Batch">
                          <BuildIcon
                            sx={{
                              ml: 0.5,
                              fontSize: 14,
                              color: "info.main",
                              verticalAlign: "middle",
                            }}
                          />
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">
                        {formatDate(receive.receivedDate)}
                      </Typography>
                    </TableCell>
                    <TableCell>{receive.receivedBy}</TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight="600">
                        {receive.receivedQty?.toFixed(2)}
                      </Typography>
                    </TableCell>
                    <TableCell>{getStatusChip(receive)}</TableCell>
                    <TableCell align="right">
                      {receive.checkRecord ? (
                        <Typography
                          variant="body2"
                          color="success.main"
                          fontWeight="600"
                        >
                          {receive.checkRecord.checkedQty?.toFixed(2)}
                        </Typography>
                      ) : (
                        <Typography variant="caption" color="text.disabled">
                          -
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {receive.checkRecord ? (
                        <Typography
                          variant="body2"
                          color="error.main"
                          fontWeight="600"
                        >
                          {receive.checkRecord.damagedQty?.toFixed(2)}
                        </Typography>
                      ) : (
                        <Typography variant="caption" color="text.disabled">
                          -
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {(() => {
                        const rem = getRemainingQty(receive);
                        return rem > 0 ? (
                          <Typography
                            variant="body2"
                            color="warning.main"
                            fontWeight="600"
                          >
                            {rem.toFixed(2)}
                          </Typography>
                        ) : (
                          <Typography variant="caption" color="text.disabled">
                            -
                          </Typography>
                        );
                      })()}
                    </TableCell>
                    <TableCell>
                      {receive.checkRecord?.checkedBy || (
                        <Typography variant="caption" color="text.disabled">
                          -
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {receive.checkRecord ? (
                        <Typography variant="caption">
                          {formatDate(receive.checkRecord.checkedDate)}
                        </Typography>
                      ) : (
                        <Typography variant="caption" color="text.disabled">
                          -
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {getRemainingQty(receive) > 0 && (
                        <Tooltip
                          title={
                            receive.checkRecord
                              ? `${getRemainingQty(receive).toFixed(2)} items not yet tested`
                              : "Check This Batch"
                          }
                        >
                          <Button
                            size="small"
                            variant="outlined"
                            color={receive.checkRecord ? "warning" : "success"}
                            startIcon={<CheckCircleIcon />}
                            onClick={() => onCheckBatch(receive)}
                          >
                            {receive.checkRecord ? "Continue Check" : "Check"}
                          </Button>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ReceivingHistoryDialog;
